import React, { useState, useEffect } from 'react';
import {
  X,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Check,
  Clock,
  Building2,
  Plus,
  Mail,
  Eye,
  Download,
  Edit,
  Filter,
  Search,
  Calendar,
  CreditCard,
  FileText,
  Users,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { collection, getDocs, addDoc, updateDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase/client';
import InvoiceGenerator from './InvoiceGenerator';

interface BillingDashboardProps {
  onClose: () => void;
}

export default function BillingDashboard({ onClose }: BillingDashboardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'invoices' | 'pricing'>('overview');
  const [companies, setCompanies] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const [billingStats, setBillingStats] = useState({
    totalRevenue: 0,
    totalOutstanding: 0,
    totalInvoices: 0,
    paidInvoices: 0,
    unpaidInvoices: 0,
    overdueInvoices: 0,
    mrr: 0,
  });

  const [pricingForm, setPricingForm] = useState({
    pricePerWell: 50,
    billingFrequency: 'monthly',
    freeTrialEnabled: true,
    freeTrialDays: 30,
    discountPercent: 0,
    minimumCharge: 0,
    notes: '',
  });

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    setLoading(true);
    try {
      // Load companies
      const companiesRef = collection(db, 'companies');
      const companiesSnapshot = await getDocs(companiesRef);
      const fetchedCompanies = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().companyName || 'Unknown',
        companyName: doc.data().companyName,
        adminEmail: doc.data().contactEmail,
        wellCount: doc.data().wellCount || 0,
        pricePerWell: doc.data().pricePerWell || 5, // Default $5
        freeTrialEnabled: doc.data().freeTrialEnabled || false,
        freeTrialEndDate: doc.data().freeTrialEndDate,
        // Calculate totals dynamically from invoices later if needed, or store aggregates
        totalInvoiced: 0,
        totalPaid: 0,
        totalOutstanding: 0
      }));

      // Load invoices
      const invoicesRef = collection(db, 'invoices');
      const invoicesQuery = query(invoicesRef, orderBy('createdAt', 'desc'));
      const invoicesSnapshot = await getDocs(invoicesQuery);
      const fetchedInvoices = invoicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Process stats
      let totalRevenue = 0;
      let totalOutstanding = 0;
      let paidCount = 0;
      let unpaidCount = 0;
      let overdueCount = 0;

      fetchedInvoices.forEach((inv: any) => {
        if (inv.status === 'paid') {
          totalRevenue += inv.total || 0;
          paidCount++;
        } else {
          totalOutstanding += inv.total || 0;
          unpaidCount++;
          if (inv.status === 'overdue') overdueCount++;
        }
      });

      // Update company aggregates
      const companiesWithStats = fetchedCompanies.map(comp => {
        const compInvoices = fetchedInvoices.filter((inv: any) => inv.companyId === comp.id);
        const compTotalInvoiced = compInvoices.reduce((sum: number, inv: any) => sum + (inv.total || 0), 0);
        const compTotalPaid = compInvoices
          .filter((inv: any) => inv.status === 'paid')
          .reduce((sum: number, inv: any) => sum + (inv.total || 0), 0);
        const compTotalOutstanding = compInvoices
          .filter((inv: any) => inv.status !== 'paid')
          .reduce((sum: number, inv: any) => sum + (inv.total || 0), 0);

        return {
          ...comp,
          totalInvoiced: compTotalInvoiced,
          totalPaid: compTotalPaid,
          totalOutstanding: compTotalOutstanding,
          lastInvoiceDate: compInvoices.length > 0 ? compInvoices[0].issueDate : null
        };
      });

      setCompanies(companiesWithStats);
      setInvoices(fetchedInvoices);
      setBillingStats({
        totalRevenue,
        totalOutstanding,
        totalInvoices: fetchedInvoices.length,
        paidInvoices: paidCount,
        unpaidInvoices: unpaidCount,
        overdueInvoices: overdueCount,
        mrr: companiesWithStats.reduce((sum, c) => sum + (c.wellCount * c.pricePerWell), 0)
      });

    } catch (error) {
      console.error('Error loading billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async (company: any) => {
    setLoading(true);
    try {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const invoiceData = {
        invoiceNumber: `INV-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}-${String(invoices.length + 1).padStart(4, '0')}`,
        companyId: company.id,
        companyName: company.name || company.companyName,
        status: 'draft',
        issueDate: now.toISOString(),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        billingPeriodStart: startDate.toISOString(),
        billingPeriodEnd: endDate.toISOString(),
        total: company.wellCount * company.pricePerWell,
        subtotal: company.wellCount * company.pricePerWell,
        discount: 0,
        tax: 0,
        lineItems: [
          {
            description: `Oil Well Management - ${company.wellCount} Wells`,
            quantity: company.wellCount,
            unitPrice: company.pricePerWell,
            total: company.wellCount * company.pricePerWell,
          },
        ],
        createdAt: now.toISOString(),
        updatedAt: now.toISOString()
      };

      const docRef = await addDoc(collection(db, 'invoices'), invoiceData);

      const newInvoice = { ...invoiceData, id: docRef.id };
      setInvoices([newInvoice, ...invoices]);
      toast.success(`Invoice ${newInvoice.invoiceNumber} created for ${company.name}`);

      // Refresh to update stats
      loadBillingData();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (invoice: any) => {
    try {
      const paidDate = new Date().toISOString();
      await updateDoc(doc(db, 'invoices', invoice.id), {
        status: 'paid',
        paidDate: paidDate,
        updatedAt: new Date().toISOString()
      });

      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === invoice.id
            ? { ...inv, status: 'paid', paidDate: paidDate }
            : inv
        )
      );
      toast.success(`Invoice ${invoice.invoiceNumber} marked as paid`);
      loadBillingData();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to update invoice');
    }
  };

  const handleSendInvoice = async (invoice: any) => {
    try {
      await updateDoc(doc(db, 'invoices', invoice.id), {
        status: 'sent',
        updatedAt: new Date().toISOString()
      });

      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === invoice.id && inv.status === 'draft'
            ? { ...inv, status: 'sent' }
            : inv
        )
      );

      const company = companies.find((c) => c.id === invoice.companyId);
      toast.success(`Invoice sent to ${company?.adminEmail}`, {
        description: 'Email with PDF attachment has been sent',
      });
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  const handleSetPricing = async () => {
    if (!selectedCompany) return;

    const freeTrialEndDate = pricingForm.freeTrialEnabled
      ? new Date(Date.now() + pricingForm.freeTrialDays * 24 * 60 * 60 * 1000).toISOString()
      : null;

    try {
      await updateDoc(doc(db, 'companies', selectedCompany.id), {
        pricePerWell: pricingForm.pricePerWell,
        freeTrialEnabled: pricingForm.freeTrialEnabled,
        freeTrialEndDate,
        updatedAt: new Date().toISOString()
      });

      setCompanies((prev) =>
        prev.map((comp) =>
          comp.id === selectedCompany.id
            ? {
              ...comp,
              pricePerWell: pricingForm.pricePerWell,
              freeTrialEnabled: pricingForm.freeTrialEnabled,
              freeTrialEndDate,
            }
            : comp
        )
      );

      toast.success(`Pricing updated for ${selectedCompany.name}`);
      setShowPricingModal(false);
      setSelectedCompany(null);
      loadBillingData();
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error('Failed to update pricing');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
      case 'sent':
        return <Badge className="bg-blue-100 text-blue-800">Sent</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-800">Overdue</Badge>;
      case 'draft':
        return <Badge className="bg-gray-100 text-gray-800">Draft</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    const matchesSearch =
      invoice.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="fixed inset-0 z-[100] bg-white overflow-y-auto">
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Billing Dashboard</h2>
              <p className="text-sm text-green-100 mt-1">
                Manage invoicing and payments
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title="Close billing dashboard"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm sm:text-base ${activeTab === 'overview'
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
          >
            <TrendingUp className="h-4 w-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm sm:text-base ${activeTab === 'invoices'
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Invoices
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm sm:text-base ${activeTab === 'pricing'
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
          >
            <DollarSign className="h-4 w-4 inline mr-2" />
            Pricing
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-700">Total Revenue</p>
                        <p className="text-2xl font-bold text-green-900 mt-1">
                          {formatCurrency(billingStats.totalRevenue)}
                        </p>
                      </div>
                      <DollarSign className="h-10 w-10 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-700">Monthly MRR</p>
                        <p className="text-2xl font-bold text-blue-900 mt-1">
                          {formatCurrency(billingStats.mrr)}
                        </p>
                      </div>
                      <TrendingUp className="h-10 w-10 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-700">Outstanding</p>
                        <p className="text-2xl font-bold text-orange-900 mt-1">
                          {formatCurrency(billingStats.totalOutstanding)}
                        </p>
                      </div>
                      <Clock className="h-10 w-10 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-red-700">Overdue</p>
                        <p className="text-2xl font-bold text-red-900 mt-1">
                          {billingStats.overdueInvoices}
                        </p>
                      </div>
                      <AlertCircle className="h-10 w-10 text-red-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Companies Billing Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>Companies Billing Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {companies.map((company) => (
                      <div
                        key={company.id}
                        className="border-2 border-gray-200 rounded-lg p-4 hover:border-green-300 transition-all"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900">
                                {company.name}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {company.wellCount} wells × {formatCurrency(company.pricePerWell)}/well
                              </p>
                            </div>
                          </div>
                          {company.freeTrialEnabled ? (
                            <Badge className="bg-purple-100 text-purple-800">
                              Free Trial
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">
                              Active
                            </Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-4 gap-3 mb-3">
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Invoiced</p>
                            <p className="font-bold text-gray-900">
                              {formatCurrency(company.totalInvoiced)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Paid</p>
                            <p className="font-bold text-green-600">
                              {formatCurrency(company.totalPaid)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Outstanding</p>
                            <p className="font-bold text-orange-600">
                              {formatCurrency(company.totalOutstanding)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Last Invoice</p>
                            <p className="text-xs font-medium text-gray-900">
                              {company.lastInvoiceDate
                                ? formatDate(company.lastInvoiceDate)
                                : 'Never'}
                            </p>
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={() => handleGenerateInvoice(company)}
                            className="flex-1 h-9 bg-green-600 hover:bg-green-700"
                            disabled={company.freeTrialEnabled}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Generate Invoice
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedCompany(company);
                              setPricingForm({
                                pricePerWell: company.pricePerWell,
                                billingFrequency: 'monthly',
                                freeTrialEnabled: company.freeTrialEnabled,
                                freeTrialDays: 30,
                                discountPercent: 0,
                                minimumCharge: 0,
                                notes: '',
                              });
                              setShowPricingModal(true);
                            }}
                            className="h-9"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>

                        {company.freeTrialEnabled && company.freeTrialEndDate && (
                          <div className="mt-3 bg-purple-50 border border-purple-200 rounded p-2 text-sm text-purple-700">
                            Free trial ends: {formatDate(company.freeTrialEndDate)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <>
              {/* Filters */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search invoices..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 h-10"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant={filterStatus === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilterStatus('all')}
                        className="h-10"
                      >
                        All
                      </Button>
                      <Button
                        variant={filterStatus === 'sent' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilterStatus('sent')}
                        className="h-10"
                      >
                        Sent
                      </Button>
                      <Button
                        variant={filterStatus === 'paid' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilterStatus('paid')}
                        className="h-10"
                      >
                        Paid
                      </Button>
                      <Button
                        variant={filterStatus === 'overdue' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilterStatus('overdue')}
                        className="h-10"
                      >
                        Overdue
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Invoices List */}
              <Card>
                <CardHeader>
                  <CardTitle>All Invoices ({filteredInvoices.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredInvoices.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No invoices found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredInvoices.map((invoice) => (
                        <div
                          key={invoice.id}
                          className="border-2 border-gray-200 rounded-lg p-4 hover:border-green-300 transition-all"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center space-x-3 mb-1">
                                <h3 className="font-bold text-gray-900">
                                  {invoice.invoiceNumber}
                                </h3>
                                {getStatusBadge(invoice.status)}
                              </div>
                              <p className="text-sm text-gray-600">
                                {invoice.companyName}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-gray-900">
                                {formatCurrency(invoice.total)}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                            <div>
                              <p className="text-gray-600">Issue Date</p>
                              <p className="font-medium text-gray-900">
                                {formatDate(invoice.issueDate)}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Due Date</p>
                              <p className="font-medium text-gray-900">
                                {formatDate(invoice.dueDate)}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Paid Date</p>
                              <p className="font-medium text-gray-900">
                                {invoice.paidDate ? formatDate(invoice.paidDate) : '-'}
                              </p>
                            </div>
                          </div>

                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setShowInvoicePreview(true);
                              }}
                              className="flex-1 h-9"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            {invoice.status === 'draft' && (
                              <Button
                                size="sm"
                                onClick={() => handleSendInvoice(invoice)}
                                className="flex-1 h-9 bg-blue-600 hover:bg-blue-700"
                              >
                                <Mail className="h-4 w-4 mr-1" />
                                Send
                              </Button>
                            )}
                            {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                              <Button
                                size="sm"
                                onClick={() => handleMarkAsPaid(invoice)}
                                className="flex-1 h-9 bg-green-600 hover:bg-green-700"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Mark Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <Card>
              <CardHeader>
                <CardTitle>Company Pricing Plans</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {companies.map((company) => (
                    <div
                      key={company.id}
                      className="border-2 border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">
                              {company.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {company.wellCount} wells
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">
                            {formatCurrency(company.pricePerWell)}
                            <span className="text-sm text-gray-600">/well</span>
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            {formatCurrency(company.wellCount * company.pricePerWell)}/month total
                          </p>
                        </div>
                      </div>

                      {company.freeTrialEnabled && (
                        <div className="mt-3 bg-purple-50 border border-purple-200 rounded p-3">
                          <p className="text-sm font-medium text-purple-900">
                            Free Trial Active
                          </p>
                          {company.freeTrialEndDate && (
                            <p className="text-xs text-purple-700 mt-1">
                              Ends: {formatDate(company.freeTrialEndDate)}
                            </p>
                          )}
                        </div>
                      )}

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedCompany(company);
                          setPricingForm({
                            pricePerWell: company.pricePerWell,
                            billingFrequency: 'monthly',
                            freeTrialEnabled: company.freeTrialEnabled,
                            freeTrialDays: 30,
                            discountPercent: 0,
                            minimumCharge: 0,
                            notes: '',
                          });
                          setShowPricingModal(true);
                        }}
                        className="w-full mt-3"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Pricing
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Pricing Modal */}
        {showPricingModal && selectedCompany && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Set Pricing for {selectedCompany.name}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Price per Well (Monthly)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      value={pricingForm.pricePerWell}
                      onChange={(e) =>
                        setPricingForm((prev) => ({
                          ...prev,
                          pricePerWell: parseFloat(e.target.value) || 0,
                        }))
                      }
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div>
                    <p className="font-medium text-gray-900">Free Trial</p>
                    <p className="text-xs text-gray-600">
                      Enable free trial period
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setPricingForm((prev) => ({
                        ...prev,
                        freeTrialEnabled: !prev.freeTrialEnabled,
                      }))
                    }
                    className={`relative w-14 h-8 rounded-full transition-colors ${pricingForm.freeTrialEnabled ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${pricingForm.freeTrialEnabled
                        ? 'translate-x-7'
                        : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>

                {pricingForm.freeTrialEnabled && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Free Trial Days
                    </label>
                    <Input
                      type="number"
                      value={pricingForm.freeTrialDays}
                      onChange={(e) =>
                        setPricingForm((prev) => ({
                          ...prev,
                          freeTrialDays: parseInt(e.target.value) || 0,
                        }))
                      }
                      className="h-12"
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Discount Percent (Optional)
                  </label>
                  <Input
                    type="number"
                    value={pricingForm.discountPercent}
                    onChange={(e) =>
                      setPricingForm((prev) => ({
                        ...prev,
                        discountPercent: parseFloat(e.target.value) || 0,
                      }))
                    }
                    placeholder="0"
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Minimum Monthly Charge (Optional)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      value={pricingForm.minimumCharge}
                      onChange={(e) =>
                        setPricingForm((prev) => ({
                          ...prev,
                          minimumCharge: parseFloat(e.target.value) || 0,
                        }))
                      }
                      placeholder="0"
                      className="pl-10 h-12"
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPricingModal(false);
                    setSelectedCompany(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSetPricing}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Save Pricing
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Preview */}
        {showInvoicePreview && selectedInvoice && (
          <InvoiceGenerator
            invoice={selectedInvoice}
            company={companies.find((c) => c.id === selectedInvoice.companyId)}
            onClose={() => {
              setShowInvoicePreview(false);
              setSelectedInvoice(null);
            }}
            onSendEmail={() => handleSendInvoice(selectedInvoice)}
          />
        )}
      </div>
    </div>
  );
}
